import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import PhotoCard from './PhotoCard';
import LoadingSpinner from './LoadingSpinner';
import Pagination from './Pagination';
import FilterBar from './FilterBar';
import { fetchPhotos } from '../services/api';
import './PhotoGrid.css';

const PhotoGrid = ({ filterState, onFilterApply, onFilterClear, updateFilterState }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationError, setPaginationError] = useState(null);

  // Use filter state from props
  const { activeFilter, filterLoading, filterError } = filterState;
  const [localFilterError, setLocalFilterError] = useState(null);
  
  // Track when URL parameter restoration is complete
  const [urlParamsRestored, setUrlParamsRestored] = useState(false);

  // Pagination state
  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    totalPages: null,
    nextToken: null,
    currentPageToken: null, // Token that was used to load current page
    previousTokens: [], // Stack to track previous page tokens
    hasNextPage: false,
    hasPreviousPage: false,
    photosPerPage: 9
  });

  // Ref to access current pagination state without creating dependencies
  const paginationStateRef = useRef(paginationState);
  paginationStateRef.current = paginationState;

  // Helper function to reset pagination to initial state
  const resetPagination = useCallback(() => {
    setPaginationState({
      currentPage: 1,
      totalPages: null,
      nextToken: null,
      currentPageToken: null,
      previousTokens: [],
      hasNextPage: false,
      hasPreviousPage: false,
      photosPerPage: 9
    });
  }, []);



  /**
   * Token stack management for previous page navigation
   * Each token in the stack represents the token needed to get TO that page
   */

  /**
   * Push a token onto the stack for backward navigation
   * @param {string|null} token - The pagination token to store
   */
  const pushTokenToStack = useCallback((token) => {
    console.log('📥 Pushing token to stack:', token);
    setPaginationState(prevState => ({
      ...prevState,
      previousTokens: [...prevState.previousTokens, token]
    }));
  }, []);

  /**
   * Pop a token from the stack for backward navigation
   * @returns {string|null} The previous page token
   */
  const popTokenFromStack = useCallback(() => {
    let poppedToken = null;
    setPaginationState(prevState => {
      const newTokens = [...prevState.previousTokens];
      poppedToken = newTokens.pop() || null;
      console.log('📤 Popped token from stack:', poppedToken);
      console.log('📤 Remaining tokens:', newTokens);
      return {
        ...prevState,
        previousTokens: newTokens
      };
    });
    return poppedToken;
  }, []);

  /**
   * Clear the entire token stack (used when resetting pagination)
   */
  const clearTokenStack = useCallback(() => {
    console.log('🗑️ Clearing token stack');
    setPaginationState(prevState => ({
      ...prevState,
      previousTokens: []
    }));
  }, []);

  // Session storage key for filter state
  const FILTER_STORAGE_KEY = 'airpics_filter_state';

  // Helper function to save filter state to session storage
  const saveFilterState = useCallback((filter) => {
    try {
      if (filter) {
        const filterData = {
          ...filter,
          timestamp: Date.now()
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterData));
      } else {
        // Clear session storage when filter is null
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Failed to save filter state to session storage:', error);
      // Gracefully handle storage errors - continue without session storage
    }
  }, []);

  // Helper function to restore filter state from session storage
  const restoreFilterState = useCallback(() => {
    try {
      const storedFilter = sessionStorage.getItem(FILTER_STORAGE_KEY);
      if (storedFilter) {
        const filterData = JSON.parse(storedFilter);
        
        // Check if stored data is not too old (expire after 1 hour)
        const isExpired = Date.now() - (filterData.timestamp || 0) > 60 * 60 * 1000;
        
        if (!isExpired && filterData.type && filterData.value) {
          // Remove timestamp before returning filter object
          const { timestamp, ...filter } = filterData;
          return filter;
        } else {
          // Remove expired filter data
          sessionStorage.removeItem(FILTER_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.warn('Failed to restore filter state from session storage:', error);
      // Gracefully handle storage errors - continue without session storage
      try {
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
      } catch (cleanupError) {
        // Ignore cleanup errors
      }
    }
    return null;
  }, []);

  // Initialize pagination and filter state from URL parameters on component mount
  useEffect(() => {
    const page = searchParams.get('page');
    const filterType = searchParams.get('filterType');
    const filterValue = searchParams.get('filterValue');

    let restoredFilter = null;

    // Only restore filter state from URL parameters
    if (filterType && filterValue) {
      const filterTypeConfigs = {
        airport: 'Airport',
        operator: 'Operator',
        aircraftType: 'Aircraft Type',
        registration: 'Registration',
        category: 'Category'
      };

      restoredFilter = {
        type: filterType,
        value: decodeURIComponent(filterValue),
        displayLabel: `${filterTypeConfigs[filterType] || filterType}: ${decodeURIComponent(filterValue)}`
      };
    }
    // If no URL parameters, don't restore from session storage

    // Set the restored filter state
    if (restoredFilter) {
      onFilterApply(restoredFilter);
      // Save to session storage to refresh timestamp
      saveFilterState(restoredFilter);
    }

    // Restore pagination state if page parameter exists
    if (page && !isNaN(parseInt(page))) {
      const pageNum = parseInt(page);
      if (pageNum > 1) {
        // Try to restore pagination tokens from sessionStorage
        const storedPaginationKey = (filterType && filterValue) ? `pagination_${filterType}_${decodeURIComponent(filterValue)}` : 'pagination_all';
        const storedPagination = sessionStorage.getItem(storedPaginationKey);

        if (storedPagination) {
          try {
            const paginationData = JSON.parse(storedPagination);
            // Check if stored data is not too old (expire after 1 hour)
            const isExpired = Date.now() - (paginationData.timestamp || 0) > 60 * 60 * 1000;

            if (paginationData.currentPage === pageNum && !isExpired) {
              setPaginationState(prevState => ({
                ...prevState,
                currentPage: pageNum,
                nextToken: paginationData.nextToken,
                currentPageToken: paginationData.currentPageToken,
                previousTokens: paginationData.previousTokens || [],
                hasNextPage: paginationData.hasNextPage || false,
                hasPreviousPage: pageNum > 1,
                totalPages: paginationData.totalPages
              }));
            }
          } catch (e) {
            // If parsing fails, just start from page 1
          }
        }
      }
    }
    
    // Mark URL parameter restoration as complete
    setUrlParamsRestored(true);
  }, [searchParams, onFilterApply]); // Re-run when URL params change

  // Save pagination state to sessionStorage
  const savePaginationState = useCallback((pageNum, nextToken, currentPageToken, previousTokens, hasNextPage, totalPages, filter = null) => {
    const paginationKey = filter ? `pagination_${filter.type}_${filter.value}` : 'pagination_all';

    const paginationData = {
      currentPage: pageNum,
      nextToken,
      currentPageToken,
      previousTokens,
      hasNextPage,
      totalPages,
      timestamp: Date.now()
    };

    sessionStorage.setItem(paginationKey, JSON.stringify(paginationData));
  }, []);

  // Update URL parameters with current state
  const updateURLParams = useCallback((page = null, filter = null) => {
    const params = {};

    // Add filter params if filter is active
    const currentFilter = filter !== undefined ? filter : activeFilter;
    if (currentFilter) {
      params.filterType = currentFilter.type;
      params.filterValue = encodeURIComponent(currentFilter.value);
    }

    // Add page param if not on first page
    const currentPage = page || paginationState.currentPage;
    if (currentPage > 1) {
      params.page = currentPage.toString();
    }

    setSearchParams(params);
  }, [paginationState.currentPage, activeFilter, setSearchParams]);

  /**
   * Load photos with pagination support
   * @param {string} pageDirection - Direction of pagination ('initial', 'next', 'previous')
   * @param {string|null} token - Pagination token to use
   * @param {number} retryCount - Current retry attempt count
   * @param {object|null} filter - Filter to apply (uses activeFilter if not provided)
   * @param {boolean} isFilterOperation - Whether this is a filter operation (affects error handling)
   */
  const loadPhotos = useCallback(async (pageDirection = 'initial', token = null, retryCount = 0, filter = undefined, isFilterOperation = false) => {
    const maxRetries = 3;
    const isInitialLoad = pageDirection === 'initial';

    try {
      // Set appropriate loading state
      if (isInitialLoad) {
        setLoading(true);
        setError(null);
      } else {
        setPaginationLoading(true);
        setPaginationError(null);
      }

      // Use ref to access current pagination state without creating dependencies
      const currentState = paginationStateRef.current;

      // Prepare pagination parameters based on page direction
      let paginationParams = {
        limit: currentState.photosPerPage
      };

      // Handle token based on page direction
      if (pageDirection === 'next' && token) {
        paginationParams.nextToken = token;
      } else if (pageDirection === 'previous' && token) {
        paginationParams.nextToken = token;
      } else if (pageDirection === 'initial') {
        // For initial load (including filter changes), start from first page
        paginationParams.nextToken = null;
      }

      // Use provided filter or current activeFilter
      const currentFilter = filter !== undefined ? filter : activeFilter;

      console.log('📡 API Call:', { paginationParams, pageDirection, filter: currentFilter });
      const data = await fetchPhotos(currentFilter, paginationParams);
      console.log('📥 API Response:', {
        hasPhotos: !!data.photos,
        photoCount: data.photos?.length,
        hasNextToken: !!data.nextToken,
        nextToken: data.nextToken ? data.nextToken.substring(0, 50) + '...' : null
      });

      // Handle invalid or expired pagination tokens
      if (!data || (!data.photos && !data.error)) {
        throw new Error('Invalid response from server');
      }

      // Check for specific pagination token errors
      if (data.error && data.error.includes('Invalid token')) {
        if (pageDirection !== 'initial') {
          // Reset to first page if token is invalid
          return await loadPhotos('initial', null, 0);
        }
        throw new Error('Invalid pagination token');
      }

      // Success - update photos and pagination state
      setPhotos(data.photos || []);

      // Calculate new page number and update pagination state
      let newCurrentPage;
      let newCurrentPageToken = token; // The token used to load this page

      if (pageDirection === 'next') {
        newCurrentPage = currentState.currentPage + 1;
      } else if (pageDirection === 'previous') {
        newCurrentPage = currentState.currentPage - 1;
      } else {
        // Initial load or filter change - reset to page 1
        newCurrentPage = 1;
        newCurrentPageToken = null; // First page has no token
      }

      // Update pagination state with new data
      setPaginationState(prevState => {
        const newState = {
          ...prevState,
          currentPage: newCurrentPage,
          nextToken: data.nextToken || null,
          currentPageToken: newCurrentPageToken,
          hasNextPage: !!data.nextToken,
          hasPreviousPage: newCurrentPage > 1,
          totalPages: data.total ? Math.ceil(data.total / currentState.photosPerPage) : null,
          // Clear token stack only on initial load
          previousTokens: pageDirection === 'initial' ? [] : prevState.previousTokens
        };
        
        console.log('🔄 Updating pagination state:', newState);
        console.log('🔄 Token stack:', newState.previousTokens);
        return newState;
      });

      // Update URL with current page
      updateURLParams(newCurrentPage);

      // Save pagination state to sessionStorage
      savePaginationState(
        newCurrentPage,
        data.nextToken || null,
        newCurrentPageToken,
        pageDirection === 'initial' ? [] : currentState.previousTokens,
        !!data.nextToken,
        data.total ? Math.ceil(data.total / currentState.photosPerPage) : null,
        currentFilter
      );

    } catch (err) {
      console.error('Error loading photos:', err);

      // Handle invalid token error
      if (err.message.includes('Invalid token')) {
        if (pageDirection !== 'initial') {
          // Reset to first page if token is invalid
          return await loadPhotos('initial', null, 0, filter, isFilterOperation);
        }
      }

      // Handle different types of errors
      let errorMessage = 'Failed to load photos. Please try again.';

      if (err.message.includes('Invalid token')) {
        errorMessage = 'Page expired. Returning to first page.';
      } else if (err.message.includes('Network') || err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again.';
      } else if (err.message.includes('Server error')) {
        errorMessage = 'Server error. Please try again later.';
      }

      // Retry logic for transient errors (not token errors)
      if (retryCount < maxRetries && !err.message.includes('Invalid token') && !isFilterOperation) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          loadPhotos(pageDirection, token, retryCount + 1, filter, isFilterOperation);
        }, delay);
        return; // Don't set error state yet, wait for retry
      }

      // Set appropriate error state after retries exhausted
      if (isFilterOperation) {
        // For filter operations, re-throw the error so it can be handled by the filter handler
        throw err;
      } else if (isInitialLoad) {
        setError(errorMessage);
        setPhotos([]);
      } else {
        setPaginationError(errorMessage);
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setPaginationLoading(false);
      }
    }
  }, [updateURLParams, savePaginationState]);

  // Load photos after URL parameter restoration is complete
  useEffect(() => {
    if (urlParamsRestored) {
      // This effect runs after URL parameters have been processed
      // and activeFilter state has been set
      loadPhotos('initial');
    }
  }, [urlParamsRestored]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for activeFilter changes and trigger new loads
  useEffect(() => {
    if (urlParamsRestored) {
      // Reset pagination and load photos when filter changes
      resetPagination();
      loadPhotos('initial');
    }
  }, [activeFilter, urlParamsRestored]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter application
  const handleFilterApply = useCallback(async (filter) => {
    console.log('🔍 Applying filter:', filter);
    
    // Set filter loading state
    updateFilterState({ loading: true });
    setLocalFilterError(null);
    
    try {
      // Update active filter state
      onFilterApply(filter);
      
      // Save filter state to session storage
      saveFilterState(filter);
      
      // Reset pagination to page 1 when filter changes
      resetPagination();
      
      // Load photos with new filter
      await loadPhotos('initial', null, 0, filter, true);
      
      // Update URL with filter parameters (page will be 1)
      updateURLParams(1, filter);
      
    } catch (error) {
      console.error('Error applying filter:', error);
      setLocalFilterError('Failed to apply filter. Please try again.');
      // Reset filter state on error
      onFilterClear();
      // Clear session storage on error
      saveFilterState(null);
    } finally {
      updateFilterState({ loading: false });
    }
  }, [resetPagination, loadPhotos, updateURLParams, saveFilterState, onFilterApply, onFilterClear, updateFilterState]);

  // Handle filter clear
  const handleFilterClear = useCallback(async () => {
    console.log('🔍 Clearing filter');
    
    // Set filter loading state
    updateFilterState({ loading: true });
    setLocalFilterError(null);
    
    try {
      // Clear active filter state first
      onFilterClear();
      
      // Clear filter state from session storage
      saveFilterState(null);
      
      // Clear URL parameters immediately
      setSearchParams({});
      
      // Reset pagination to page 1 when filter is cleared
      resetPagination();
      
      // Load all photos (no filter)
      await loadPhotos('initial', null, 0, null, true);
      
    } catch (error) {
      console.error('Error clearing filter:', error);
      setLocalFilterError('Failed to clear filter. Please try again.');
    } finally {
      updateFilterState({ loading: false });
    }
  }, [resetPagination, loadPhotos, setSearchParams, saveFilterState, onFilterClear, updateFilterState]);





  // Handle navigation to next page
  const handleNextPage = async () => {
    console.log('🔄 Next page clicked', {
      hasNextPage: paginationState.hasNextPage,
      loading,
      paginationLoading,
      nextToken: paginationState.nextToken,
      currentPage: paginationState.currentPage,
      currentPageToken: paginationState.currentPageToken
    });

    if (!paginationState.hasNextPage || loading || paginationLoading) {
      console.log('❌ Next page blocked');
      return;
    }

    // Store current page's token in stack for back navigation
    // This is the token needed to get back to the current page
    pushTokenToStack(paginationState.currentPageToken);

    // Load next page using the nextToken
    await loadPhotos('next', paginationState.nextToken);
  };

  // Handle navigation to previous page
  const handlePreviousPage = async () => {
    console.log('🔄 Previous page clicked', {
      hasPreviousPage: paginationState.hasPreviousPage,
      loading,
      paginationLoading,
      currentPage: paginationState.currentPage,
      tokenStack: paginationState.previousTokens
    });

    if (!paginationState.hasPreviousPage || loading || paginationLoading) {
      console.log('❌ Previous page blocked');
      return;
    }

    // Get the token for the previous page from stack
    const previousPageToken = popTokenFromStack();
    console.log('🔄 Using previous page token:', previousPageToken);

    // Load previous page
    await loadPhotos('previous', previousPageToken);
  };

  // Handle navigation to first page
  const handleFirstPage = async () => {
    if (!paginationState.hasPreviousPage || loading || paginationLoading) {
      return;
    }

    // Clear the token stack and reset pagination state
    clearTokenStack();
    resetPagination();
    await loadPhotos('initial');

    // Update URL (removes page parameter)
    updateURLParams(1);
  };

  // Retry failed pagination request
  const retryPagination = () => {
    setPaginationError(null);
    // Retry the last attempted page navigation
    if (paginationState.hasNextPage) {
      handleNextPage();
    } else if (paginationState.hasPreviousPage) {
      handlePreviousPage();
    }
  };

  // Retry failed initial load
  const retryInitialLoad = () => {
    setError(null);
    loadPhotos('initial');
  };

  console.log('🔍 Render state:', { loading, error, photosCount: photos.length });
  
  if (loading) return <LoadingSpinner />;

  // Determine if pagination should be shown
  // Show pagination if we have a next page OR we're not on the first page
  // This handles cases where we have exactly 9 photos but there might be more
  const shouldShowPagination = paginationState.hasNextPage || paginationState.hasPreviousPage;
  
  console.log('🔍 Pagination state:', {
    currentPage: paginationState.currentPage,
    hasNextPage: paginationState.hasNextPage,
    hasPreviousPage: paginationState.hasPreviousPage,
    shouldShowPagination,
    nextToken: paginationState.nextToken,
    photosCount: photos.length
  });

  return (
    <div className="photo-grid-container">
      {/* Filter Error Display */}
      {(filterError || localFilterError) && (
        <div className="filter-error" role="alert" aria-live="assertive">
          <div className="error-text">{filterError || localFilterError}</div>
          <button
            className="retry-button"
            onClick={() => {
              setLocalFilterError(null);
              if (activeFilter) {
                handleFilterApply(activeFilter);
              } else {
                handleFilterClear();
              }
            }}
            aria-label="Retry filter operation"
          >
            Try Again
          </button>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="error-message" role="alert" aria-live="assertive">
          <div className="error-text">{error}</div>
          <button
            className="retry-button"
            onClick={retryInitialLoad}
            aria-label="Retry loading photos"
          >
            Try Again
          </button>
        </div>
      ) : photos.length === 0 && !loading ? (
        <div className="no-photos" role="status" aria-live="polite">
          {activeFilter ? `No photos found for ${activeFilter.displayLabel}` : 'No photos found.'}
        </div>
      ) : (
        <>
          <main
            className="photo-grid"
            role="main"
            aria-label={`Photo gallery showing ${photos.length} photos${activeFilter ? ` filtered by ${activeFilter.displayLabel}` : ''}${paginationState.currentPage > 1 ? ` on page ${paginationState.currentPage}` : ''}`}
          >
            {photos.map((photo) => (
              <PhotoCard key={photo.PK} photo={photo} />
            ))}
          </main>
          {shouldShowPagination && (
            <>
              {paginationError && (
                <div className="pagination-error" role="alert" aria-live="assertive">
                  <div className="error-text">{paginationError}</div>
                  <button
                    className="retry-button"
                    onClick={retryPagination}
                    aria-label="Retry pagination request"
                  >
                    Retry
                  </button>
                </div>
              )}
              <Pagination
                currentPage={paginationState.currentPage}
                totalPages={paginationState.totalPages}
                hasNextPage={paginationState.hasNextPage}
                hasPreviousPage={paginationState.hasPreviousPage}
                onNextPage={handleNextPage}
                onPreviousPage={handlePreviousPage}
                onFirstPage={handleFirstPage}
                loading={paginationLoading}
                error={paginationError}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PhotoGrid;